import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Quiz from "@/components/Quiz";
import {
  Minus,
  Plus,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ArrowLeft,
  RefreshCw,
  Sprout,
  BookOpenText,
  Info,
} from "lucide-react";
import { t } from "@/i18n";

const SOURCES = [
  { id: "both", l: "Mots + verbes" },
  { id: "vocab", l: "Vocabulaire" },
  { id: "verbs", l: "Verbes" },
];
const MODES = [
  { id: "mixed", l: "Mélangé" },
  { id: "extract", l: "Trouver la racine" },
  { id: "family", l: "Trouver le mot" },
];

const DIFF_LABELS = {
  1: "Très courant",
  2: "Courant",
  3: "Intermédiaire",
  4: "Avancé",
  5: "Expert",
};

// Route de la fiche mémo intégrée au système de Fiches mémo existant
// (Cheatsheets.jsx ouvre automatiquement l'id passé en paramètre ?open=).
const ROOTS_MEMO_URL = "/cheatsheets?open=cs_root_full";

export default function PracticeRoots() {
  const navigate = useNavigate();
  const [count, setCount] = useState(10);
  const [source, setSource] = useState("both");
  const [mode, setMode] = useState("mixed");
  const [diffMin, setDiffMin] = useState(1);
  const [diffMax, setDiffMax] = useState(5);
  const [avail, setAvail] = useState(0);
  const [session, setSession] = useState(null);
  const [done, setDone] = useState(null);

  const load = async () => {
    const { data } = await api.get(
      `/practice/roots?count=1&source=${source}&mode=${mode}&difficulty_min=${diffMin}&difficulty_max=${diffMax}`,
    );
    setAvail(data.available);
  };
  useEffect(() => {
    load();
  }, [source, mode, diffMin, diffMax]);

  const preset = (kind) => {
    if (kind === "beginner") {
      setMode("extract");
      setSource("vocab");
      setDiffMin(1);
      setDiffMax(2);
    } else {
      setMode("mixed");
      setSource("both");
      setDiffMin(1);
      setDiffMax(5);
    }
  };

  const start = async () => {
    const { data } = await api.get(
      `/practice/roots?count=${count}&source=${source}&mode=${mode}&difficulty_min=${diffMin}&difficulty_max=${diffMax}`,
    );
    setSession(data.questions.map((q) => ({ ...q, skill: "vocabulary" })));
    setDone(null);
  };

  // — Quiz en cours —
  if (session && !done) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-head text-2xl font-bold">{t("practiceroots.racines")}</h1>
          <button
            onClick={() => navigate(ROOTS_MEMO_URL)}
            className="flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            <BookOpenText size={15} /> {t("practiceroots.fiche_memo")}
          </button>
        </div>
        <Quiz
          questions={session}
          onDone={(r) => setDone(r)}
          arabicOptions
          rewardMode="normal"
          rewardAmount={1}
        />
      </div>
    );
  }

  // — Résultats —
  if (done) {
    const score = Math.round((done.correct / done.total) * 100);
    const passed = score >= 80;
    return (
      <div className="max-w-xl mx-auto text-center">
        <div
          className="rounded-2xl border bg-card p-8 mb-4"
          data-testid="roots-result"
        >
          {passed ? (
            <CheckCircle2
              size={52}
              className="mx-auto mb-3"
              style={{ color: "#16A34A" }}
            />
          ) : (
            <XCircle size={52} className="mx-auto mb-3 text-destructive" />
          )}
          <h2 className="font-head text-2xl font-bold mb-1">
            {t("practiceconjugation.session_terminee")}
          </h2>
          <p className="text-muted-foreground mb-4">
            {passed
              ? t("practiceroots.bien_joue_tu_commences_a_voir")
              : t("practiceroots.continue_les_racines_demandent_de_la")}
          </p>
          <div className="mb-4">
            <div className="flex justify-between text-sm text-muted-foreground mb-1">
              <span>{done.correct} correctes</span>
              <span>{done.total - done.correct} {t("lessonpage.ratees")}</span>
            </div>
            <div className="h-3 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${score}%`,
                  backgroundColor: passed
                    ? "#16A34A"
                    : "hsl(var(--destructive))",
                }}
              />
            </div>
          </div>
          <div
            className="text-4xl font-head font-bold mb-1"
            style={{ color: passed ? "#16A34A" : undefined }}
          >
            {score}%
          </div>
          <div className="text-sm text-muted-foreground mb-6">
            {done.correct} / {done.total} {t("interexerciserecap.bonnes_reponses")}
          </div>

          {!passed && (
            <button
              onClick={() => navigate(ROOTS_MEMO_URL)}
              className="w-full mb-3 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold hover:bg-amber-500/25 transition"
            >
              <BookOpenText size={16} /> {t("practiceroots.revoir_la_fiche_memo_sur_les")}
            </button>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setDone(null)}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
            >
              <RotateCcw size={16} /> {t("practiceconjugation.memes_questions")}
            </button>
            <button
              onClick={() => {
                setDone(null);
                setSession(null);
              }}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border font-medium hover:bg-secondary transition"
            >
              <RefreshCw size={16} /> {t("practiceconjugation.nouvelle_selection")}
            </button>
            <button
              onClick={() => navigate("/practice")}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border font-medium hover:bg-secondary transition"
            >
              <ArrowLeft size={16} /> {t("alphabetmodules.retour")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // — Configuration —
  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="font-head text-3xl font-bold flex items-center gap-2">
          <Sprout size={26} className="text-primary" />
          {t("practiceroots.entrainement_aux_racines")}
        </h1>
        <button
          onClick={() => navigate(ROOTS_MEMO_URL)}
          data-testid="roots-memo-link"
          className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium hover:bg-secondary transition"
        >
          <BookOpenText size={15} /> {t("practiceroots.fiche_memo")}
        </button>
      </div>

      <div className="rounded-2xl border bg-primary/5 p-4 mb-6 flex gap-3">
        <Info size={18} className="text-primary shrink-0 mt-0.5" />
        <p className="text-sm">
          {t("practiceroots.cet_exercice_t_entraine_a_reperer")} <b>racine</b>{t("practiceroots.et_a_reconnaitre_les_mots_d")}{" "}
          <button
            onClick={() => navigate(ROOTS_MEMO_URL)}
            className="text-primary underline font-medium"
          >
            {t("practiceroots.fiche_memo_2")}
          </button>
          .
        </p>
      </div>

      <div className="flex gap-3 mb-6">
        <button
          data-testid="preset-beginner"
          onClick={() => preset("beginner")}
          className="px-4 py-2 rounded-xl border font-medium hover:bg-secondary transition"
        >
          {t("practiceconjugation.debutant")}
        </button>
        <button
          data-testid="preset-full"
          onClick={() => preset("full")}
          className="px-4 py-2 rounded-xl border font-medium hover:bg-secondary transition"
        >
          {t("practiceconjugation.complet")}
        </button>
        <div
          className="ml-auto text-sm text-muted-foreground self-center"
          data-testid="avail-count"
        >
          {avail} {t("practiceconjugation.questions_disponibles")}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border bg-card p-4">
          <div className="text-sm font-medium mb-1">{t("practiceroots.source")}</div>
          <div className="text-xs text-muted-foreground mb-2">
            {t("practiceroots.d_ou_viennent_les_mots_testes")}
          </div>
          <div className="flex flex-wrap gap-2">
            {SOURCES.map((s) => (
              <button
                key={s.id}
                data-testid={`source-${s.id}`}
                onClick={() => setSource(s.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${source === s.id ? "bg-primary text-primary-foreground border-primary" : ""}`}
              >
                {s.l}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-4">
          <div className="text-sm font-medium mb-1">{t("practiceroots.type_de_question")}</div>
          <div className="text-xs text-muted-foreground mb-2">
            {t("practiceroots.trouver_la_racine_dechiffrage_trouver_le")}
          </div>
          <div className="flex flex-wrap gap-2">
            {MODES.map((m) => (
              <button
                key={m.id}
                data-testid={`mode-${m.id}`}
                onClick={() => setMode(m.id)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${mode === m.id ? "bg-primary text-primary-foreground border-primary" : ""}`}
              >
                {m.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4 mb-6">
        <div className="text-sm font-medium mb-1">
          {t("practiceroots.difficulte_du_vocabulaire_teste")}
        </div>
        <div className="text-xs text-muted-foreground mb-3">
          {t("practiceroots.ne_concerne_pas_la_complexite_de")}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-center gap-1">
            <input
              type="number"
              min={1}
              max={5}
              value={diffMin}
              onChange={(e) =>
                setDiffMin(Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 1)))
              }
              className="w-16 px-2 py-1.5 rounded-lg border bg-background text-center"
            />
            <span className="text-xs text-muted-foreground">{DIFF_LABELS[diffMin]}</span>
          </div>
          <span className="text-muted-foreground">à</span>
          <div className="flex flex-col items-center gap-1">
            <input
              type="number"
              min={1}
              max={5}
              value={diffMax}
              onChange={(e) =>
                setDiffMax(Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 5)))
              }
              className="w-16 px-2 py-1.5 rounded-lg border bg-background text-center"
            />
            <span className="text-xs text-muted-foreground">{DIFF_LABELS[diffMax]}</span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4 mb-6 flex items-center gap-3">
        <span className="text-sm font-medium">{t("coursequestions.nombre_de_questions")}</span>
        <button
          data-testid="roots-minus"
          onClick={() => setCount((c) => Math.max(5, c - 5))}
          className="w-9 h-9 rounded-lg border flex items-center justify-center"
        >
          <Minus size={16} />
        </button>
        <span className="font-head text-xl font-bold w-8 text-center">
          {count}
        </span>
        <button
          data-testid="roots-plus"
          onClick={() => setCount((c) => c + 5)}
          className="w-9 h-9 rounded-lg border flex items-center justify-center"
        >
          <Plus size={16} />
        </button>
      </div>

      <button
        data-testid="start-roots"
        disabled={avail === 0}
        onClick={start}
        className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition disabled:opacity-40"
      >
        {t("practiceconjugation.demarrer_la_pratique")}
      </button>
    </div>
  );
}
